import { isCodeColumnName, resolveBoundColumnPair } from '@inference.engine/taxonomy/web';

/**
 * Parses clipboard / pasted spreadsheet text into a grid.
 *
 * Excel, Google Sheets and Numbers all put TAB-separated text on the clipboard,
 * so tabs win when present; otherwise we fall back to commas so a raw CSV paste
 * also works. Quoted CSV cells (`"a,b"`) are respected, and doubled quotes
 * inside a quoted cell unescape to one.
 *
 * `delimiter: '\t'` turns the comma fallback off, for a paste straight from a spreadsheet: one
 * copied cell holding "Korea, Republic of" arrives with no tab and must stay one value.
 * `keepBlankRows` keeps leading and inner blank rows, so a copied column with an empty cell still
 * lines up with the rows it is pasted onto. Trailing blank rows are still dropped: a selection
 * that runs past the data copies them, and keeping them would clear the rows below.
 */
export function parseTabular(
  text: string,
  {
    delimiter: wanted = 'auto',
    keepBlankRows = false,
  }: { delimiter?: 'auto' | '\t'; keepBlankRows?: boolean } = {},
): string[][] {
  const normalized = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  if (!normalized) return [];

  const delimiter = wanted === '\t' || normalized.includes('\t') ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i += 1) {
    const char = normalized[i];

    if (inQuotes) {
      if (char === '"') {
        if (normalized[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"' && cell === '') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = '';
    } else if (char === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }
  row.push(cell);
  rows.push(row);

  const isBlank = (r: string[]) => r.every((c) => c.trim() === '');
  if (keepBlankRows) {
    while (rows.length > 0 && isBlank(rows[rows.length - 1])) rows.pop();
    return rows;
  }
  // Drop rows that are entirely blank (trailing newlines, stray separators).
  return rows.filter((r) => !isBlank(r));
}

/**
 * Does the first parsed row look like a HEADER rather than data?
 *
 * Answered from evidence — the row NAMES the columns — rather than from the
 * shape of the data. The previous rule asked whether any first-row cell
 * repeated further down its own column, which a controlled vocabulary never
 * does: its values are distinct by definition. So it ticked for essentially
 * every real paste and swallowed the first row, applying 249 of 250 countries.
 *
 * No evidence means no header, which is the safe direction: a missed header
 * costs one click, a swallowed row is invisible until someone notices the
 * vocabulary is short. Used only to pre-tick the box; the user can override.
 */
export function looksLikeHeader(grid: string[][], columnNames: string[]): boolean {
  if (grid.length < 2) return false;

  // Selecting whole columns in Excel copies a trailing tab, so a header row that
  // names the columns exactly arrives one cell too wide. Dropping trailing blanks
  // costs nothing — a blank can never match a column name, which is required to
  // be non-empty — and without it the commonest paste of all fails on a character
  // the user cannot see.
  const header = [...grid[0]];
  while (header.length > 0 && header[header.length - 1].trim() === '') header.pop();

  if (header.length !== columnNames.length) return false;
  return header.every(
    (cell, col) => cell.trim().toLowerCase() === columnNames[col].trim().toLowerCase(),
  );
}

/**
 * First-row names that read as a copied list's code column title: "Code", "ISO code", "Abbrev",
 * "Short code". Whole-cell and narrow, because this decides on its own to treat the first row as
 * a header, and a vocabulary that starts with "Short", "QR code" or "Zip code" must not lose its
 * first value. The header switch stays editable either way.
 */
const COPIED_CODE_HEADER =
  /^((iso|country|market|platform|channel|short) ?)?codes?$|^abbrev(iation)?s?$|^short ?name$/i;

/** Whether the first row looks like a copied list's header, so the header switch starts on. */
export function looksLikeCopiedListHeader(grid: string[][]): boolean {
  return grid.length >= 2 && grid[0].some((cell) => COPIED_CODE_HEADER.test(cell.trim()));
}

/**
 * The positions of the Value and Code columns in a header row, picked by the shared pair rule the
 * list itself uses, so "ISO 3166 code" or "Ad platform code" is read as Code wherever it sits.
 * Undefined when no header cell names a code column, which leaves the paste read by position.
 */
export function valueAndCodePositions(
  header: string[],
): { value: number; code: number } | undefined {
  if (!header.some(isCodeColumnName)) return undefined;
  const pair = resolveBoundColumnPair(header.map((name, index) => ({ id: String(index), name })));
  return pair
    ? { value: Number(pair.valueColumn.id), code: Number(pair.codeColumn.id) }
    : undefined;
}
